import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { HiSearch, HiOutlineX } from 'react-icons/hi'
import useDebounce from 'hooks/useDebounce'
import usePush from 'hooks/usePush'
import { classNames } from 'helpers/utilities'

interface FiltersProps {
  search: string
  placeholder?: string
  className?: string
}

export default function Filters({ search, placeholder = 'Search', className = '' }: FiltersProps) {
  const [searchText, setSearchText] = useState(search)

  const debouncedSearchText = useDebounce(searchText, 500)

  const { pathname, query, isReady } = useRouter()
  const push = usePush()

  useEffect(() => {
    if (isReady && debouncedSearchText !== search) {
      const { search: _, ...params } = query

      push({
        pathname,
        query: {
          ...params,
          ...(debouncedSearchText !== '' ? { search: debouncedSearchText } : {})
        }
      })
    }
  }, [debouncedSearchText, isReady, pathname, push, query, search])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const lowercase = e.target.value.toLowerCase()
    const filtered = lowercase.replace(/[^a-z0-9\.]/gi, '')
    setSearchText(filtered)
  }

  const resetInput = () => {
    setSearchText('')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      resetInput()
    }
  }

  return (
    <div
      className={classNames(
        'bg-gray-100 pt-4 pb-5 px-4 sm:px-6 lg:px-8 -mx-4 sm:-mx-6 lg:-mx-8 dark:bg-gray-950',
        className
      )}
    >
      <div>
        <label htmlFor="dashboard-search" className="sr-only">
          {placeholder}
        </label>
        <div className="relative w-full rounded-md shadow-sm max-w-sm text-gray-400 focus-within:text-gray-600 dark:text-gray-600 dark:focus-within:text-gray-400">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <HiSearch className="h-5 w-5" aria-hidden="true" />
          </div>
          <input
            id="dashboard-search"
            type="text"
            value={searchText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            autoCorrect="off"
            className="w-full rounded-md border-white sm:text-sm shadow-sm px-10 placeholder-gray-500 focus:outline-none focus:ring-brand-500 focus:border-brand-500 dark:bg-gray-850 dark:border-transparent dark:text-gray-100 dark:focus:border-brand-500 dark:placeholder-gray-500 dark:caret-gray-400"
            placeholder={placeholder}
          />
          {search && search.length > 0 && (
            <div className="absolute inset-y-0 right-2 flex items-center">
              <button
                type="button"
                className="inline-flex items-center justify-center border border-transparent rounded-full text-gray-400 bg-white hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 focus:text-gray-600 dark:bg-transparent dark:text-gray-500 dark:hover:text-gray-400 dark:focus:ring-offset-gray-800"
                onClick={resetInput}
              >
                <HiOutlineX className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
